/**
 * Der Revisionsstrom: die erste Ansicht, die ein Entwickler öffnet.
 *
 * Über ihr steht die **Abdeckung** (`CompletionSection`): der Anteil der Revisionen,
 * für die von einem Agenten eine vollständige Begutachtung vorliegt. Sie ist die
 * Zusage, mit der dieses Werkzeug beschrieben wird, und gehört deshalb an diese
 * Ansicht und nicht in die Verwaltung — wer die Liste liest, soll wissen, wie
 * viel von ihr überhaupt angesehen wurde.
 *
 * Eine seitenweise Liste der eingegangenen Revisionen, neueste zuerst. Vier
 * Angaben sind der Grund, warum sie mehr ist als eine Liste:
 *
 * - das **Wärmebild** — wie viel hier liegt, wie schwer es wiegt und wie einig
 *   sich die Agenten an der schwersten Stelle sind;
 * - die **Review-Empfehlung** — dorthin soll die Aufmerksamkeit gehen, und wo
 *   sie ohne Befund steht, ist sie hervorgehoben: Das ist die eine Aussage, die
 *   nur sie treffen kann;
 * - der **Bearbeitungsstand**, damit niemand auf ein Ergebnis wartet, das nie
 *   kommt;
 * - die **eigene Abgabe**, damit erkennbar ist, wo man selbst steht.
 *
 * **Aktualisiert wird durch wiederholtes Fragen** (ADR-0008), nicht über eine
 * dauerhaft offene Verbindung: Unter Apache mit prefork bindet jede offene
 * Verbindung einen Worker-Prozess, und ein hängender Client blockiert ihn
 * dauerhaft. Bei zehn Nutzern und diesem Datenvolumen ist Fragen unsichtbar
 * billig.
 *
 * Gefiltert wird im Server. Die Oberfläche hält keine zweite, eigene Liste, aus
 * der sie herausrechnet — sonst stimmte die Seitenzahl nicht mehr mit dem
 * überein, was zu sehen ist.
 *
 * **Die Bauform: Liste mit vielen Filtern und Blättern** (#41). Die
 * **Werkzeugleiste** trägt links die eine Handlung auf den ganzen Ausschnitt —
 * das Zurücksetzen — und rechts den Seitennavigator. Die sieben **Filterachsen
 * bleiben im Inhalt**: Sie wählen aus, *was* in der Liste steht, und sieben
 * Auswahlfelder passen weder in ein Band von 49 Punkten noch gehören sie dorthin.
 * Der **Zähler** steht im Kopf des Abschnitts, den er zählt.
 *
 * Der Seitennavigator steht seither **einmal** statt zweimal. Zwei standen hier,
 * weil beide wegscrollten; der in der Leiste tut es nicht.
 */

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";

import { Button, Chip, DataTable, Select, StatusPill } from "../design-system";
import type { DataTableColumn } from "../design-system";
import type {
  RevisionHeat,
  RevisionState,
  RevisionStream,
  RevisionStreamApi,
  RevisionStreamQuery,
  RevisionSummary,
  Severity,
} from "../api/client";
import { formatAge, formatNumber, formatTimestamp } from "../formatting";
import { STREAM_START, type StreamFilter } from "../navigation/place";
import { Toolbar } from "../shell/Toolbar";
import { WithdrawBlindButton } from "./blindReview";
import { DeliveryMark } from "./delivery";
import { VisitedMarker } from "./visited";
import { NumericCell } from "./cells";
import { CompletionSection } from "./CompletionSection";
import { FindingCell } from "./findingCell";
import type { Tone } from "./findings";
import { IssueReferenceCell } from "./issueReferenceCell";
import {
  FINDINGS_WIDTH,
  ISSUE_REFERENCE_WIDTH,
  MESSAGE_MIN_STREAM,
  PERSON_WIDTH,
  PROJECT_WIDTH,
  REVISION_WIDTH,
} from "./listWidths";
import { FilterReset, ListPage, ListSection, rangeOf } from "./list";
import { MessageCell } from "./messageCell";
import { PersonCell } from "./personCell";
import { Notice, describe, type Loaded } from "./loading";

/** Der Takt, in dem der Strom nachfragt. */
const POLL_MILLISECONDS = 5_000;

const PER_PAGE = 25;

/** Die Beschriftungen des Bearbeitungsstands — dieselben im Strom und im Filter. */
const STATE_LABELS: Record<RevisionState, string> = {
  pending: "noch nicht begutachtet",
  in_review: "in Arbeit",
  reviewed: "begutachtet",
  failed: "unvollständig begutachtet",
};

const ALL_PROJECTS = "Alle Projekte";
const ALL_IDENTITIES = "Alle Kennungen";

/**
 * Der eigene Commit — **als Eintrag im Kennungs-Select und nicht als eigener
 * Knopf** (ADR-0028).
 *
 * Es ist derselbe Filter — welcher Committer —, nur über die Person gesprochen
 * statt über eine Kennung, und weil eine Person mehrere Kennungen hat, ist es die
 * einzige Formulierung, die ein Select über einzelne Kennungen gar nicht
 * ausdrücken kann. Ein zweiter Knopf wäre außerdem das dritte „mir" in einer
 * Leiste, die schon „von mir abgegeben" trägt; so bleiben es zwei, und sie sagen
 * sauber Verschiedenes: die eigene Durchsicht gegen den eigenen Commit.
 */
const MY_IDENTITIES = "meine Kennungen";

/**
 * Dasselbe für den, dem keine SVN-Kennung zugeordnet ist: **abgeschaltet und mit
 * dem Grund im Klartext.**
 *
 * Der Grund steht in der Beschriftung und nicht in einem Titel, weil das
 * Design-System keinen abgeschalteten Eintrag kennt und keiner nachgebaut wird —
 * gewählt wird er trotzdem nicht, die Leiste stellt sich danach zurück. Weggelassen
 * wäre er die schlechtere Auskunft: Es sähe aus, als gäbe es den Filter nicht.
 *
 * „keine zugeordnet" und nicht „keine SVN-Kennung zugeordnet": Der Eintrag steht
 * im Kennungsfilter, und wovon die Rede ist, sagt das Feld, in dem er steht.
 */
const WITHOUT_IDENTITY = "meine Kennungen — keine zugeordnet";
const ANY_RECOMMENDATION = "Empfehlung: alle";
const WITH_RECOMMENDATION = "mit Review-Empfehlung";
const WITHOUT_RECOMMENDATION = "ohne Review-Empfehlung";
const ANY_STATE = "Stand: alle";
const ANY_SUBMISSION = "Abgabe: alle";
const SUBMITTED = "von mir abgegeben";
const NOT_SUBMITTED = "noch nicht abgegeben";
const ANY_SEVERITY = "Schwere: alle";
const ANY_FINDINGS = "Befunde: alle";
const WITH_FINDINGS = "mit Befund";
const WITHOUT_FINDINGS = "ohne Befund";

/**
 * Die Beschriftungen des Schweregradfilters.
 *
 * „mit einem schweren Befund" und nicht „ab schwer": Der Filter nennt einen Wert
 * der geschlossenen Menge und meint ihn — `mittel` zeigt, wo etwas Mittleres
 * liegt, und nicht zusätzlich alles Schwere.
 */
const SEVERITY_FILTERS: Record<Severity, string> = {
  schwer: "mit einem schweren Befund",
  mittel: "mit einem mittleren Befund",
  hinweis: "mit einem Hinweis",
};

/** Die Reihenfolge, in der die drei Stufen zur Wahl stehen: die schwerste zuerst. */
const SEVERITY_ORDER: Severity[] = ["schwer", "mittel", "hinweis"];

/**
 * Was in der Spalte „Befunde" steht, solange die Revision blind durchgegangen
 * wird.
 *
 * Der geschlossene Modus gilt auch für die Zahl: Auch „drei Befunde, einer
 * schwer" lenkt eine Durchsicht, und nur eine unbeeinflusste trägt die
 * Übersehen-Quote (ADR-0013).
 */
const HIDDEN_WHILE_BLIND =
  "Diese Revision gehen Sie blind durch. Fremde Befunde erscheinen erst nach Ihrer Abgabe.";

/** Der Satz zur Übereinstimmung, ausgeschrieben — im Titel, nicht in der Zeile. */
const AGREEMENT_TITLE =
  "So viele der begutachtenden Agenten tragen die schwerste Stelle, und auf so vielen " +
  "verschiedenen Basismodellen. Zwei Agenten auf demselben Basismodell irren gleich und " +
  "bestätigen einander.";

/** Die eine Aussage, die nur die Review-Empfehlung treffen kann. */
const RECOMMENDED_WITHOUT_FINDING = "auffällig, aber kein Befund";

/** Der freiwillige Weg in den geschlossenen Modus, je Revision (ADR-0013). */
const GO_BLIND = "blind";

/** Was in der Spalte steht, solange nichts abgegeben ist. */
const OPEN = "offen";

/**
 * Was der Knopf tut, ausgeschrieben — im Titel, nicht auf dem Knopf.
 *
 * „Blind" allein ist ein Wort aus dem Verfahren und sagt niemandem, was
 * passiert, der es nicht kennt. Der Einwand gilt, und beantwortet wird er nicht
 * mit einer längeren Aufschrift, sondern mit dem Umfeld: Die Erklärung steht
 * vollständig hier im Titel, und über dem Knopf steht in einer Spalte namens
 * „Meine Abgabe" das Abzeichen `offen`. Damit sagt die Spalte, worum es geht,
 * und der Knopf nur noch, wohin er führt — auf zwei Wörtern stünde dieselbe
 * Auskunft ein zweites Mal und kostete die Breite, die der Kommentarvorschau
 * fehlt.
 */
const GO_BLIND_TITLE =
  "Diese Revision unbeeinflusst durchgehen: Bis zu Ihrer Abgabe bleiben fremde Befunde " +
  "verborgen — auch ihre Zahl. Nach der Abgabe erscheinen sie, und der Vergleich mit Ihren " +
  "eigenen zeigt, was Sie übersehen haben. Zurücknehmen können Sie das, solange Sie nicht " +
  "abgegeben haben.";

const ALREADY_DISCLOSED =
  "Zu dieser Revision sind Ihnen bereits fremde Befunde ausgeliefert worden. " +
  "Blind geht nur, solange nichts Fremdes ausgeliefert wurde.";

/**
 * Die gesetzten Filter ohne die Seite. `null` heißt bei jedem: danach wird nicht
 * gefiltert.
 *
 * Sie stehen zusammen mit der Seite als `StreamFilter` in der Adresse — dort
 * liegt auch die Vorbelegung (`navigation/place`). Hier steht nur, was die
 * Ansicht davon von Hand ändert.
 */
type Chosen = Omit<StreamFilter, "page">;

/** Der Ausschnitt ohne die Seite — die Seite führt die Ansicht getrennt. */
function chosenOf(filter: StreamFilter): Chosen {
  const { page: _page, ...chosen } = filter;

  return chosen;
}

/**
 * Die Vorbelegung dieser Leiste: keine Achse gesetzt (`STREAM_START`). Gegen sie
 * wird verglichen, und auf sie stellt „Filter zurücksetzen" zurück; das ist
 * zugleich die Adresse `/revisions` ohne Abfrageteil.
 */
const START_CHOSEN: Chosen = chosenOf(STREAM_START);

/**
 * **Die Spalten des Stroms** — als ausgeführte Konstante und nicht im JSX.
 *
 * Zwei Gründe, und der zweite ist der wichtigere: Die Spalte sagt seit #142 nur
 * noch, **wie** die Zeile aussieht — was in ihr steht, baut die Zeilenabbildung
 * darunter; und die Zahlen, aus denen die Breite entsteht, sind so **prüfbar**
 * (`listWidths.test.ts`). `scripts/verify` sieht keine Pixel — eine abgeschnittene
 * Kopfzeile oder ein waagerechter Rollbalken fiele sonst niemandem auf, bis
 * jemand die Ansicht öffnet.
 *
 * Die Reihenfolge ist die Leserichtung der Zeile: **wo** die Revision herkommt,
 * **woran** gearbeitet wurde, **was** gefunden wurde, **wie** es um sie steht.
 *
 * Jede Spalte trägt eine Breite; ungedeckelt ist genau eine — der Kommentar.
 * Die Regel und ihre Rechnung stehen in `listWidths.ts`.
 */
export const STREAM_COLUMNS: DataTableColumn[] = [
  { key: "here", label: "", width: 24 },
  // `Rev.` und nicht „Revision": Die Zahl käme mit siebzig Pixeln aus, das
  // ausgeschriebene Wort verlangte achtzig. Die Abkürzung trägt dafür ihren
  // `hint` — so hält es das Design-System bei jeder abgekürzten Kopfzeile. Mit
  // dem `r` davor steht die Revision hier genauso da wie im Befundvorrat.
  {
    key: "number",
    label: "Rev.",
    hint: "Revision",
    align: "right",
    width: REVISION_WIDTH,
  },
  // Sechsundachtzig Pixel: Der Kopf „Zeitpunkt" bestimmt sie, nicht der Inhalt —
  // der ist mit `13 Std` kürzer und mit `10.07.2026` gerade so lang
  // (`formatAge`).
  { key: "committed", label: "Zeitpunkt", width: 86 },
  // Projekt und Entwickler tragen dieselben Breiten wie im Befundvorrat — derselbe
  // Projektname und derselbe Mensch sollen in beiden Listen an derselben Stelle
  // abbrechen. Abgeschnitten werden beide im Titel ganz gelesen.
  {
    key: "project",
    label: "Projekt",
    maxWidth: PROJECT_WIDTH,
    width: PROJECT_WIDTH,
  },
  // **Die Kopfzeile heißt „Entwickler", der Schlüssel bleibt `person`** (#129,
  // ADR-0009): Bezeichner folgen dem Glossarbegriff — dahinter steht die Person,
  // aufgelöst über ihre SVN-Kennung —, die Kopfzeile ist Oberflächentext und
  // trägt das konkretere Wort. „Person" ist im Glossar jeder Angemeldete, auch
  // das `management`, das nie committet; hier steht der, der die Revision
  // übergeben hat.
  {
    key: "person",
    label: "Entwickler",
    maxWidth: PERSON_WIDTH,
    width: PERSON_WIDTH,
  },
  // **Die eine ungedeckelte Spalte dieser Liste.** Ihr `width` ist ein Boden und
  // kein Maß: Sie bekommt, was die übrigen Spalten übrig lassen, und ist die
  // einzige, die von mehr Platz auch mehr Auskunft macht. Ein `maxWidth` wäre
  // genau die Obergrenze, die sie auf breiten Schirmen am Wachsen hinderte.
  {
    key: "message",
    label: "Kommentar",
    width: MESSAGE_MIN_STREAM,
  },
  // **Neben der Vorschau und nicht in ihr** (#113): Der Klick auf die Zeile
  // öffnet die Revision, und ein Link *in* der Vorschau gäbe derselben Stelle
  // zwei Ziele. Zwei Ziele in zwei Spalten sind dagegen eine Tabelle.
  // Unmittelbar hinter dem Kommentar, aus dem der Verweis stammt — dazwischen
  // gehört nichts.
  //
  // **Mit Deckel** (#129): Was hier steht, ist nicht auf eine Länge festgelegt —
  // das Kürzel der Ticketliste ist kurz (`DEV-435`, #131), ohne eingetragenes
  // steht die lange Form `owner/repo#nummer` da, und beides kommt in derselben
  // Spalte vor. Ohne
  // `maxWidth` gäbe es an der Zelle kein `overflow: hidden`, die Spalte wüchse
  // über ihre Zuweisung und zöge die Liste hinter sich her; `maxWidth: "100%"` an
  // der Zelle begrenzt nichts, weil ein Prozentwert in die intrinsische Breite
  // einer Tabellenzelle nicht eingeht.
  {
    key: "issues",
    label: "Ticket",
    maxWidth: ISSUE_REFERENCE_WIDTH,
    width: ISSUE_REFERENCE_WIDTH,
  },
  {
    key: "findings",
    label: "Befunde",
    width: FINDINGS_WIDTH,
  },
  // Hundertvierundachtzig Pixel sind an diesen beiden Spalten keine Wahl,
  // sondern die Breite ihres längsten Abzeichens: „auffällig, aber kein Befund"
  // und „unvollständig begutachtet". Eine `StatusPill` bricht nicht um — zu knapp
  // gesetzt spränge die Spalte, und der Unterschied ginge der Kommentarvorschau
  // ab. Die Beschriftungen selbst bleiben: Sie stehen zugleich im Filter und im
  // Kopf der Revisionsansicht (`STATE_LABELS`), und eine zweite Beschriftung für
  // denselben Zustand wäre der teurere Handel.
  {
    key: "recommendation",
    label: "Review-Empfehlung",
    width: 184,
  },
  {
    key: "state",
    label: "Bearbeitungsstand",
    width: 184,
  },
  // Der Kopf „Meine Abgabe" bestimmt die Breite: Darunter steht immer ein
  // Abzeichen — `abgegeben`, `blind` oder `offen` — und im offenen Fall der
  // schmale Knopf `blind` daneben.
  {
    key: "submission",
    label: "Meine Abgabe",
    width: 105,
  },
  // Die **Zustellung** bekommt eine eigene, schmale Spalte und wird nicht in eine
  // der drei daneben gelegt (#102): Sie ist weder Bearbeitungsstand noch Abgabe
  // noch Nachhalten, und in einer fremden Spalte wäre sie genau die
  // Verwechslung, gegen die ADR-0027 und ADR-0012 geschrieben sind. Sie sagt
  // „darüber wurde geredet". Der Kopf bestimmt die Breite; darunter steht
  // `1 Std` mit dem Zeichen davor.
  {
    key: "delivery",
    label: "Zustellung",
    width: 92,
  },
];

/**
 * `onOpen` führt in die Revisionsansicht. Der Strom kennt sie nicht — er reicht
 * nur die Kennung nach oben, und die Hülle entscheidet, was sie zeigt.
 *
 * `filter` ist der Ausschnitt, mit dem die Liste aufgebaut wird — aus der
 * Adresse, aus einem Sprung aus den Befunden (Spec v1.3, Story 221) oder aus
 * dem Chronikeintrag, in den Zurück geführt hat. Er wird beim ersten Rendern
 * übernommen und danach nicht mehr: Wer den Filter von Hand ändert, soll ihn
 * behalten. Wer die Liste neu vorbelegt, baut sie neu auf.
 *
 * `onFilter` meldet jede Änderung nach oben, damit sie in die Adresse kommt und
 * eine Rückkehr denselben Ausschnitt zeigt. Die Liste bleibt dabei die
 * Besitzerin ihrer Filter; die Adresse ist ihr Gedächtnis, nicht ihr Vormund.
 *
 * `marked` ist die zuletzt geöffnete Zeile, die die Liste hervorhebt.
 *
 * `hasSvnIdentity` sagt, ob der fragenden Person eine SVN-Kennung zugeordnet ist
 * (ADR-0028). Ohne Zuordnung stünde hinter „meine Kennungen" immer eine leere
 * Liste, und die läse sich wie ein sauberer Stand; der Eintrag steht dann
 * abgeschaltet da und nennt den Grund. Vorbelegt ist **nein**: Wer die Auskunft
 * noch nicht hat, verspricht nichts, was er nicht halten kann.
 */
export function RevisionStreamView({
  client,
  onOpen,
  filter = STREAM_START,
  onFilter,
  marked = null,
  hasSvnIdentity = false,
}: {
  client: RevisionStreamApi;
  onOpen: (revisionId: number) => void;
  filter?: StreamFilter;
  onFilter?: (filter: StreamFilter) => void;
  marked?: string | null;
  hasSvnIdentity?: boolean;
}): ReactElement {
  const [chosen, setChosen] = useState<Chosen>(() => chosenOf(filter));
  const [page, setPage] = useState(filter.page);
  const state = usePolledStream(client, { page, perPage: PER_PAGE, ...chosen });

  // Ein geänderter Filter beginnt wieder auf der ersten Seite: Seite drei eines
  // anderen Filters ist eine andere Seite drei.
  const change = (next: Partial<Chosen>): void => {
    const merged = { ...chosen, ...next };

    setChosen(merged);
    setPage(1);
    onFilter?.({ ...merged, page: 1 });
  };

  const turn = (next: number): void => {
    setPage(next);
    onFilter?.({ ...chosen, page: next });
  };

  const stream = state.kind === "loaded" ? state.value : null;
  const pages = stream?.pages ?? 1;
  const total = stream?.total ?? 0;

  // Die Absicht wird gesetzt und die Liste zieht sie im nächsten Takt nach; ein
  // eigener Ladezustand je Zeile wäre mehr Zustand als Auskunft. Weist der
  // Server ab, steht der Grund über der Tabelle — der Knopf ist dann ohnehin
  // schon verbraucht.
  const [blindError, setBlindError] = useState<string | null>(null);

  const goBlind = (revisionId: number): void => {
    setBlindError(null);

    client.requestBlindReview(revisionId).then(
      () => undefined,
      (failure: unknown) => setBlindError(describe(failure)),
    );
  };

  // Die Rücknahme geht denselben Weg: gesetzt, im nächsten Takt nachgezogen. Wer
  // in der Zwischenzeit abgegeben hat, bekommt den Grund über der Tabelle und
  // sieht die Zeile gleich darauf als abgegeben.
  const withdrawBlind = (revisionId: number): void => {
    setBlindError(null);

    client.withdrawBlindReview(revisionId).then(
      () => undefined,
      (failure: unknown) => setBlindError(describe(failure)),
    );
  };

  return (
    <>
      {/*
        **Die Werkzeugleiste dieser Ansicht** (#41). Links steht die eine Handlung
        auf den ganzen Ausschnitt — das Zurücksetzen —, rechts das Blättern. Die
        sieben Filterachsen bleiben im Inhalt: Sie wählen aus, *was* in der Liste
        steht, und ein Band von 49 Punkten trägt sie nicht.
      */}
      <Toolbar
        paging={
          total === 0
            ? null
            : { page, pages, rangeLabel: rangeOf(page, PER_PAGE, total), onTurn: turn }
        }
      >
        <FilterReset chosen={chosen} start={START_CHOSEN} onReset={() => change(START_CHOSEN)} />
      </Toolbar>

      <ListPage>
        {/*
          Die Abdeckung steht **über** dem Strom und nicht unter ihm: Sie sagt, wie
          viel von dem, was hier steht, überhaupt angesehen wurde — und wer das
          erst nach der Liste erfährt, hat die Liste bereits geglaubt.
        */}
        <CompletionSection client={client} />

        <ListSection
          title="Revisionen"
          glyph="layers"
          headerRight={stream === null ? null : <Chip>{`${formatNumber(total)} Revisionen`}</Chip>}
          filters={
            <FilterBar
              stream={stream}
              chosen={chosen}
              change={change}
              hasSvnIdentity={hasSvnIdentity}
            />
          }
        >
          <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
            {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
            {state.kind === "failed" ? (
              <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
            ) : null}
            {stream !== null && stream.revisions.length === 0 ? (
              <Notice text="Keine Revision im Strom." />
            ) : null}
            {blindError === null ? null : (
              <Notice text={blindError} glyph="triangle-alert" color="var(--ac-red)" />
            )}
          </div>

          {stream !== null && stream.revisions.length > 0 ? (
            <DataTable
              /*
                Die Zeile trägt seit #142 keine Kennung mehr, sondern nur ihre
                Zellen — geöffnet wird deshalb über den Platz in derselben Liste,
                aus der die Zeilen entstanden sind. Das ist kein Umweg: Beides
                kommt aus `stream.revisions`, und dieselbe Antwort zeichnet die
                Liste und beantwortet den Klick.
              */
              onRowClick={(_row, index) => {
                const revision = stream.revisions[index];

                if (revision !== undefined) {
                  onOpen(revision.id);
                }
              }}
              columns={STREAM_COLUMNS}
              rows={stream.revisions.map((revision) => ({
                cells: {
                  here: <VisitedMarker shown={marked === String(revision.id)} />,
                  // `r4.711` und nicht `4.711`: Dieselbe Schreibweise wie im
                  // Befundvorrat — die Zeile sagt damit von sich aus, dass die Zahl
                  // eine Revision ist, und die Kopfzeile darf `Rev.` heißen.
                  number: <NumericCell>{`r${formatNumber(revision.number)}`}</NumericCell>,
                  committed: <CommittedCell at={revision.committedAt} />,
                  project: <ProjectCell name={revision.projectName} />,
                  message: <MessageCell text={revision.message} />,
                  issues: <IssueReferenceCell references={revision.issueReferences} />,
                  person: (
                    <PersonCell svnIdentity={revision.svnIdentity} personName={revision.personName} />
                  ),
                  findings: <FindingsCell heat={revision.findings} />,
                  recommendation: <RecommendationCell revision={revision} />,
                  state: <StateBadge state={revision.state} />,
                  delivery: <DeliveryMark at={revision.deliveredAt} />,
                  submission: (
                    <SubmissionCell
                      revision={revision}
                      onGoBlind={goBlind}
                      onWithdrawBlind={withdrawBlind}
                    />
                  ),
                },
              }))}
            />
          ) : null}
        </ListSection>
      </ListPage>
    </>
  );
}

/**
 * Die Filterleiste. Die Auswahl kommt aus derselben Antwort wie die Zeilen und
 * bleibt vollständig, auch wenn gerade gefiltert wird — sonst nähme ein gesetzter
 * Filter die Wege zurück, ihn zu wechseln.
 *
 * **Der Knopf zum Zurücksetzen steht nicht mehr hier**, sondern in der
 * Werkzeugleiste (#41): Er wählt nichts aus, sondern stellt den ganzen Ausschnitt
 * zurück — eine Handlung auf den Screen und keine Achse. Nebenbei hat er dort
 * einen festen Platz, statt am Ende einer umbrechenden Reihe zu erscheinen und
 * wieder zu verschwinden.
 */
function FilterBar({
  stream,
  chosen,
  change,
  hasSvnIdentity,
}: {
  stream: RevisionStream | null;
  chosen: Chosen;
  change: (next: Partial<Chosen>) => void;
  hasSvnIdentity: boolean;
}): ReactElement {
  const projects = stream?.projects ?? [];
  const identities = stream?.svnIdentities ?? [];
  const project = projects.find((one) => one.id === chosen.projectId);

  return (
    <div
      role="group"
      aria-label="Filter"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-4)",
        padding: "var(--sp-2) var(--sp-5) var(--sp-5)",
      }}
    >
      {/*
        Die Breiten der Filterfelder bleiben die gemessenen und wandern nur vom
        eigenen Prop des abgelösten Auswahlfelds in dessen `style`: Das V3-Feld
        führt kein `width`. Ein Token gibt es dafür in keiner der beiden
        Generationen — die Zahl ist je Filter am längsten Eintrag abgelesen, und
        ein Token je Filter wäre keines. Zur Bemessung siehe #140.
      */}
      <Select
        style={{ width: 190 }}
        height={28}
        value={project?.name ?? ALL_PROJECTS}
        options={[ALL_PROJECTS, ...projects.map((one) => one.name)]}
        onSelect={(picked) =>
          change({ projectId: projects.find((one) => one.name === picked)?.id ?? null })
        }
      />
      {/*
        Ein Select für beide Fassungen derselben Frage: „meine Kennungen" zuerst,
        dann „Alle Kennungen", dann die einzelnen. Wer eine einzelne wählt, nimmt
        den eigenen Commit damit zurück — zwei gesetzte Antworten in einem Feld
        wären zwei Filter, von denen man nur einen sieht.

        Eine abgewiesene Auswahl wird schlicht verworfen und braucht keinen
        Schlüssel, der das Feld neu aufbaut: Das Auswahlfeld des Skills zeigt
        allein den gesetzten Wert und merkt sich nicht, was zuletzt angeklickt
        wurde. Das abgelöste tat es — ohne diesen Schlüssel stand dann „meine
        Kennungen" in der Leiste, während die Liste weiter alle Kennungen zeigte.
      */}
      <Select
        // Breiter genau dort, wo der abgeschaltete Eintrag steht: Er ist der
        // längste in dieser Liste, und sein Grund gehört nicht in einen
        // waagerechten Rollbalken. Sonst bleibt die Leiste, wie sie war.
        style={{ width: hasSvnIdentity ? 180 : 240 }}
        height={28}
        value={chosen.committer === "me" ? MY_IDENTITIES : (chosen.svnIdentity ?? ALL_IDENTITIES)}
        options={[hasSvnIdentity ? MY_IDENTITIES : WITHOUT_IDENTITY, ALL_IDENTITIES, ...identities]}
        onSelect={(picked) => {
          if (picked === WITHOUT_IDENTITY) {
            return;
          }

          change({
            committer: picked === MY_IDENTITIES ? "me" : null,
            svnIdentity: picked === MY_IDENTITIES || picked === ALL_IDENTITIES ? null : picked,
          });
        }}
      />
      <Select
        style={{ width: 210 }}
        height={28}
        value={labelOfRecommendation(chosen.reviewRecommended)}
        options={[ANY_RECOMMENDATION, WITH_RECOMMENDATION, WITHOUT_RECOMMENDATION]}
        onSelect={(picked) =>
          change({
            reviewRecommended:
              picked === WITH_RECOMMENDATION ? true : picked === WITHOUT_RECOMMENDATION ? false : null,
          })
        }
      />
      <Select
        style={{ width: 210 }}
        height={28}
        value={chosen.state === null ? ANY_STATE : STATE_LABELS[chosen.state]}
        options={[ANY_STATE, ...stateOrder().map((one) => STATE_LABELS[one])]}
        onSelect={(picked) => change({ state: stateOf(picked) })}
      />
      <Select
        style={{ width: 190 }}
        height={28}
        value={labelOfSubmission(chosen.submitted)}
        options={[ANY_SUBMISSION, SUBMITTED, NOT_SUBMITTED]}
        onSelect={(picked) =>
          change({ submitted: picked === SUBMITTED ? true : picked === NOT_SUBMITTED ? false : null })
        }
      />
      <Select
        style={{ width: 190 }}
        height={28}
        value={labelOfFindings(chosen.hasFindings)}
        options={[ANY_FINDINGS, WITH_FINDINGS, WITHOUT_FINDINGS]}
        onSelect={(picked) =>
          change({
            hasFindings: picked === WITH_FINDINGS ? true : picked === WITHOUT_FINDINGS ? false : null,
          })
        }
      />
      <Select
        style={{ width: 230 }}
        height={28}
        value={chosen.severity === null ? ANY_SEVERITY : SEVERITY_FILTERS[chosen.severity]}
        options={[ANY_SEVERITY, ...SEVERITY_ORDER.map((one) => SEVERITY_FILTERS[one])]}
        onSelect={(picked) => change({ severity: severityOf(picked) })}
      />
    </div>
  );
}

/**
 * Das **Wärmebild** einer Zeile: wie viel hier liegt, wovon es handelt und wer es
 * gesehen hat (Spec v1.3, Stories 210 und 211).
 *
 * **Dieselbe Zelle wie im Befundvorrat**, aus `FindingCell`: Zahl und Farbe mit
 * dem Titel in der ersten Zeile, die Namen mit dem Zeichen „Regel" in der
 * zweiten. Beide Listen zeigen denselben Fund, und zwei Aufbauten wären zwei
 * Antworten auf dieselbe Frage.
 *
 * **Der Grad steht in der Farbe und nicht daneben als Wort.** „3 · schwer" nahm
 * dem Titel den Platz in derselben Zeile, obwohl das Schild die Schwere schon
 * trägt; in Buchstaben steht sie im Titel der Zahl.
 *
 * **Wer** etwas gesehen hat, steht mit Namen und nicht als „2 von 3 Agenten · 2
 * Basismodelle": Die Zahl der Befunde steht schon oben in der Zeile, und ob ein
 * Fund von zwei Agenten kommt oder von einer Regel, entscheidet mit, wie ernst
 * die Zeile zu nehmen ist (ADR-0010).
 *
 * `null` ist nicht Null: Es heißt, dass hier nichts hinausgeht, weil diese
 * Revision blind durchgegangen wird.
 */
function FindingsCell({ heat }: { heat: RevisionHeat | null }): ReactElement {
  if (heat === null) {
    return (
      <span style={{ color: "var(--fg-6)" }} title={HIDDEN_WHILE_BLIND}>
        —
      </span>
    );
  }

  if (heat.total === 0) {
    return <span style={{ color: "var(--fg-6)" }}>kein Befund</span>;
  }

  return (
    <FindingCell
      total={heat.total}
      severity={heat.severity}
      title={heat.title}
      authors={heat.authors}
      countTitle={heat.severity === null ? undefined : `Schwerster Grad: ${heat.severity}`}
    />
  );
}

/**
 * Die Review-Empfehlung — hervorgehoben genau dort, wo sie den Befunden
 * widerspricht (Spec v1.3, Story 212).
 *
 * „Auffällig, aber kein Befund" ist die eine Aussage, die nur sie treffen kann:
 * Sie zeigt auf Dinge, für die noch niemand eine Form benennen konnte. Solange
 * das Wärmebild zurückgehalten wird, bleibt es bei der gewöhnlichen Markierung —
 * ob sie widerspricht, wäre dann geraten.
 */
function RecommendationCell({ revision }: { revision: RevisionSummary }): ReactElement {
  if (!revision.reviewRecommended) {
    return <span style={{ color: "var(--fg-6)" }}>—</span>;
  }

  if (revision.findings !== null && revision.findings.total === 0) {
    return <StatusPill tone="red">{RECOMMENDED_WITHOUT_FINDING}</StatusPill>;
  }

  return <StatusPill tone="amber">Review empfohlen</StatusPill>;
}

/**
 * Wie die Zelle „Meine Abgabe" zwei Dinge übereinanderstellt: der Stand, darunter
 * der Knopf, der ihn ändert. Die Spalte ist hundertachtzig Pixel breit, und beide
 * Knöpfe darin sind breiter als der Text über ihnen.
 */
const STACKED: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "var(--sp-3)",
};

/**
 * Die eigene Abgabe — und, solange es keine gibt, der freiwillige Weg in den
 * geschlossenen Modus (ADR-0013).
 *
 * Beides in **einer** Zelle und keine eigene Spalte: Sie beantworten dieselbe
 * Frage, nämlich wo man selbst in dieser Revision steht.
 *
 * Der Knopf wird **ausgegraut** und nicht verborgen, sobald der Revisionszugang
 * eine Auslieferung vermerkt hat: Verborgen sähe es aus, als gäbe es den Weg
 * nie — er ist für diese eine Revision nur verbraucht. Nach der Abgabe steht er
 * gar nicht mehr da; blind zu arbeiten wäre dann eine Absicht über eine
 * Durchsicht, die schon abgeschlossen ist.
 *
 * Neben dem Abzeichen „blind" steht der **Weg zurück** (v1.3-N-11) — aber nur,
 * wo die Blindheit aus der Absicht kommt: Wer die Eigenschaft `blind_review`
 * trägt, hat nichts gedrückt, was zurückzunehmen wäre. Untereinander und nicht
 * nebeneinander, wie im Fall ohne Abgabe: Die Spalte ist schmal, und der Knopf
 * ist breiter als das Abzeichen davor.
 */
function SubmissionCell({
  revision,
  onGoBlind,
  onWithdrawBlind,
}: {
  revision: RevisionSummary;
  onGoBlind: (revisionId: number) => void;
  onWithdrawBlind: (revisionId: number) => void;
}): ReactElement {
  if (revision.submitted) {
    return <StatusPill tone="green">abgegeben</StatusPill>;
  }

  if (revision.blind) {
    return (
      <div style={STACKED}>
        <StatusPill tone="blue">blind</StatusPill>
        {revision.blindRequested ? (
          <WithdrawBlindButton onWithdraw={() => onWithdrawBlind(revision.id)} />
        ) : null}
      </div>
    );
  }

  return (
    <div style={STACKED}>
      {/*
        `offen` und nicht „noch nicht abgegeben": Damit steht in dieser Spalte in
        jedem Fall ein Abzeichen — `abgegeben`, `blind`, `offen` —, und sie sagt
        mit einem Blick, wo man steht. Der Filter behält seine Beschriftung
        („noch nicht abgegeben"): Dort ist es ein Satz über eine Menge, hier ein
        Zustand in einer Zeile.
      */}
      <Chip>{OPEN}</Chip>
      <Button
        variant="outline"
        size="sm"
        disabled={!revision.blindPossible}
        title={revision.blindPossible ? GO_BLIND_TITLE : ALREADY_DISCLOSED}
        icon="eye-off"
        onClick={(event) => {
          // Die Zeile öffnet sonst die Revision; hier soll der Knopf nur das
          // eine tun, was auf ihm steht.
          event.stopPropagation();
          onGoBlind(revision.id);
        }}
      >
        {GO_BLIND}
      </Button>
    </div>
  );
}

/**
 * Der Zeitpunkt der Übergabe als **Abstand zu jetzt**, der genaue Zeitpunkt im
 * Titel daneben.
 *
 * Der Strom wird gelesen, um zu sehen, was gerade hereingekommen ist: `4 Min`
 * beantwortet das, `10.08.2026, 18:22:07` verlangt dafür eine Kopfrechnung. Der
 * genaue Zeitpunkt ist deshalb nicht weg — er ist einen Mauszeiger entfernt.
 *
 * **Das Kurzformat und nicht der Satz** (`formatAge` statt `formatRelative`): In
 * einer Spalte steht kein Satz, und „vor 13 Stunden" kostet siebzig Pixel, die
 * der Kommentarvorschau daneben fehlen. Wo der Abstand in einem Satz steht — im
 * Zustelldialog, im `aria-label` der Zustellmarke —, bleibt er ausgeschrieben.
 *
 * Nachgeführt wird er vom Takt des Stroms: Er fragt ohnehin alle fünf Sekunden
 * nach und zeichnet dabei neu. Eine eigene Uhr je Zeile wäre ein zweiter Takt
 * für dieselbe Auskunft.
 */
function CommittedCell({ at }: { at: string }): ReactElement {
  return <span title={formatTimestamp(at)}>{formatAge(at)}</span>;
}

/**
 * Das Projekt — mit seinem vollen Namen im Titel.
 *
 * Die Spalte ist schmal, und Projektnamen wie `silvererp-webclient-next` sind
 * lang; abgeschnitten wurden sie auch vorher schon, nur ohne einen Weg, den Rest
 * zu lesen. Der Titel ist dieser Weg, und dieselbe Zusage macht die Spalte
 * „Entwickler" daneben.
 */
function ProjectCell({ name }: { name: string }): ReactElement {
  return <span title={name}>{name}</span>;
}

/**
 * `failed` heißt „unvollständig begutachtet" und nicht „kaputt": Die Revision
 * trägt ihre Review-Empfehlung, sie steht nur auf weniger.
 *
 * Herausgereicht, weil die Revisionsansicht denselben Stand im Kopf zeigt: Vier
 * Beschriftungen an zwei Orten wären zwei Wahrheiten.
 */
export function StateBadge({ state }: { state: RevisionState }): ReactElement {
  // Der vierte Zustand bleibt farblos und steht deshalb als `Chip` da: Er sagt,
  // dass noch nichts geschehen ist, und eine Farbe daneben läse sich wie ein
  // Ergebnis. Die drei anderen sind Zustände und tragen ihre Farbe.
  const tone: Tone | null =
    state === "reviewed" ? "green" : state === "in_review" ? "blue" : state === "failed" ? "red" : null;

  return tone === null ? (
    <Chip>{STATE_LABELS[state]}</Chip>
  ) : (
    <StatusPill tone={tone}>{STATE_LABELS[state]}</StatusPill>
  );
}

/**
 * Fragt beim Erscheinen und danach im Takt weiter.
 *
 * Die letzte Antwort bleibt stehen, solange die nächste unterwegs ist: Ein
 * Ladezustand alle fünf Sekunden wäre ein Flackern und keine Auskunft. Der Takt
 * hält an, wenn das Fenster im Hintergrund ist — genau wie der Keepalive der
 * Sitzung.
 */
function usePolledStream(
  client: RevisionStreamApi,
  query: RevisionStreamQuery,
): Loaded<RevisionStream> {
  const [state, setState] = useState<Loaded<RevisionStream>>({ kind: "loading" });
  const latest = useRef(query);
  latest.current = query;

  // Die Frage steht als Zeichenkette in der Abhängigkeit: Das Objekt entsteht
  // bei jedem Rendern neu, und ein neues Objekt startete jedes Mal einen neuen
  // Takt.
  const asked = JSON.stringify(query);

  useEffect(() => {
    let discarded = false;

    const ask = (): void => {
      client.revisions(latest.current).then(
        (value) => {
          if (!discarded) {
            setState({ kind: "loaded", value });
          }
        },
        (error: unknown) => {
          if (!discarded) {
            setState({ kind: "failed", message: describe(error) });
          }
        },
      );
    };

    ask();

    const tick = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        ask();
      }
    }, POLL_MILLISECONDS);

    return () => {
      discarded = true;
      window.clearInterval(tick);
    };
  }, [client, asked]);

  return state;
}

function labelOfRecommendation(chosen: boolean | null): string {
  if (chosen === null) {
    return ANY_RECOMMENDATION;
  }

  return chosen ? WITH_RECOMMENDATION : WITHOUT_RECOMMENDATION;
}

function labelOfSubmission(chosen: boolean | null): string {
  if (chosen === null) {
    return ANY_SUBMISSION;
  }

  return chosen ? SUBMITTED : NOT_SUBMITTED;
}

function labelOfFindings(chosen: boolean | null): string {
  if (chosen === null) {
    return ANY_FINDINGS;
  }

  return chosen ? WITH_FINDINGS : WITHOUT_FINDINGS;
}

function severityOf(label: string): Severity | null {
  return SEVERITY_ORDER.find((one) => SEVERITY_FILTERS[one] === label) ?? null;
}

/** Die Reihenfolge, in der der Bearbeitungsstand im Filter steht: der Weg einer Revision. */
function stateOrder(): RevisionState[] {
  return ["pending", "in_review", "reviewed", "failed"];
}

function stateOf(label: string): RevisionState | null {
  return stateOrder().find((one) => STATE_LABELS[one] === label) ?? null;
}
